import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Full-Slide navigation: when the sidebar is collapsed to its icon rail, hovering
 * anywhere over the rail slides the whole nav out to full width instead of showing
 * per-icon flyout popouts. It stays out while the pointer (or keyboard focus) is on
 * it, and collapses back once the user clicks through to a page.
 *
 * This hook owns only the ephemeral hover state. It never touches the persisted
 * `sidebarCollapsed` pin — the caller derives `effectiveCollapsed` from both, so the
 * grid track stays at its collapsed width and page content never reflows.
 */

// Delay before sliding out, so sweeping the pointer across the left edge on the way
// somewhere else doesn't pop the nav open.
export const OPEN_DELAY_MS = 150;

// Delay before sliding back, so briefly clipping the edge doesn't make it flicker.
export const CLOSE_DELAY_MS = 200;

export interface NavFullSlideHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

export interface UseNavFullSlideResult {
  slidOut: boolean;
  navHandlers: NavFullSlideHandlers;
  /** Collapse now and stay collapsed until the pointer actually leaves the nav. */
  collapseAfterNavigate: () => void;
}

export default function useNavFullSlide({ enabled }: { enabled: boolean }): UseNavFullSlideResult {
  const [slidOut, setSlidOut] = useState(false);

  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set when the user clicks a leaf link. Their cursor is still physically over the
  // nav at that moment, so without this latch the next mouseenter — or even the same
  // one still in flight — would slide it right back out.
  const suppressed = useRef(false);

  const clearOpen = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  const clearClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const clearBoth = () => {
    clearOpen();
    clearClose();
  };

  const scheduleClose = useCallback(() => {
    clearOpen();
    clearClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setSlidOut(false);
    }, CLOSE_DELAY_MS);
  }, []);

  const onMouseEnter = useCallback(() => {
    if (!enabled) return;
    // Re-entering while the close delay is running just cancels it — no dip closed,
    // and no fresh open delay to sit through.
    clearClose();
    if (suppressed.current || slidOut || openTimer.current) return;
    openTimer.current = setTimeout(() => {
      openTimer.current = null;
      setSlidOut(true);
    }, OPEN_DELAY_MS);
  }, [enabled, slidOut]);

  const onMouseLeave = useCallback(() => {
    if (!enabled) return;
    // A real departure ends the post-click suppression.
    suppressed.current = false;
    scheduleClose();
  }, [enabled, scheduleClose]);

  const onFocus = useCallback(() => {
    if (!enabled) return;
    // Keyboard intent is explicit — no delay, and focus overrides suppression so a
    // tab-through never leaves the labels hidden.
    clearBoth();
    suppressed.current = false;
    setSlidOut(true);
  }, [enabled]);

  const onBlur = useCallback(() => {
    if (!enabled) return;
    scheduleClose();
  }, [enabled, scheduleClose]);

  const collapseAfterNavigate = useCallback(() => {
    clearBoth();
    suppressed.current = true;
    setSlidOut(false);
  }, []);

  // Turning the preference off (or pinning the sidebar open) drops any in-flight
  // hover state rather than leaving a stale slid-out nav behind.
  useEffect(() => {
    if (enabled) return;
    clearBoth();
    suppressed.current = false;
    setSlidOut(false);
  }, [enabled]);

  useEffect(() => clearBoth, []);

  return {
    slidOut,
    navHandlers: { onMouseEnter, onMouseLeave, onFocus, onBlur },
    collapseAfterNavigate,
  };
}
